import {
  handleSubmitMultilingualForm,
  useCreateHomepage,
  useGetHomepageFormData,
  useUpdateHomepage,
} from "@/api/cms.ts";
import HomepageForm from "@/components/cms/HomepageForm.tsx";
import { CmsPageAppearanceConfig } from "@/components/cms/theme/CmsPageAppearanceConfig";
import { Switch } from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import { useAuth } from "@/context/auth/AuthContext";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import { useToast } from "@/hooks/use-toast";
import {
  constructCmsLocaleDict,
  multilingualFieldSchema,
  SupportedLocale,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout.tsx";
import { cmsPageSchema, homepageContentSchema } from "@/lib/cms/cms-types";
import {
  buildCmsPagePatchPayload,
  pageThemeFromDto,
} from "@/lib/cms/page-theme";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormFieldVisibilityProvider } from "@/context/FormFieldVisibilityContext";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const homePageLocaleSchema = cmsPageSchema
  .pick({ published: true })
  .merge(
    z.object({
      pageBackgroundColorId: z.string().nullable().optional(),
      pageTextColorMode: z
        .enum(["AUTO", "THEME_TEXT", "CUSTOM"])
        .optional()
        .default("AUTO"),
      pageTextColorId: z.string().nullable().optional(),
      pageFontFamily: z.string().nullable().optional(),
      pageFontVariant: z.string().nullable().optional(),
    }),
  )
  .merge(
    homepageContentSchema.omit({ id: true }).extend({
      websiteTitle: z.string().trim().optional(),
    }),
  )
  .superRefine((data, ctx) => {
    if (data.published && !data.websiteTitle?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "cms.form.error.pageName.empty",
        path: ["websiteTitle"],
      });
    }
  });

const homePageFormSchema = multilingualFieldSchema(
  homePageLocaleSchema.optional(),
);

export type CmsHomepageForm = z.infer<typeof homePageFormSchema>;

const CMS = () => {
  const { t } = useTranslation();
  const { instanceData } = useAuth();

  const { toast } = useToast();

  const create = useCreateHomepage();
  const update = useUpdateHomepage();
  const { data: homePageData } = useGetHomepageFormData();
  const { websiteLanguages } = useCmsWebsiteLanguages();

  const homeDefaultValues: CmsHomepageForm = useMemo(() => {
    const localeDict = constructCmsLocaleDict((lang) => {
      const home = homePageData?.[lang];
      const themeMeta = pageThemeFromDto(home);
      return {
        published: home?.published ?? false,
        ...themeMeta,
          websiteTitle: home?.content?.websiteTitle ?? "",
        websiteSubtitle: home?.content?.websiteSubtitle,
        catalogueRaisonneCTA: home?.content?.catalogueRaisonneCTA,
        artistName: home?.content?.artistName,
        birthDate: home?.content?.birthDate,
        deathDate: home?.content?.deathDate,
        credits: home?.content?.credits,
        resume: home?.content?.resume,
        footerArchives: home?.content?.footerArchives,
      };
    }, websiteLanguages);
    return localeDict;
  }, [homePageData, websiteLanguages]);

  const homeForm = useForm<CmsHomepageForm>({
    resolver: zodResolver(homePageFormSchema),
    defaultValues: homeDefaultValues,
  });

  const { reset: resetHome } = homeForm;

  const hasInitializedHome = useRef(false);

  // Only reset when we first have data; do not reset on refetch (e.g. tab focus)
  // so unsaved edits are preserved when switching browser tab or desktop.
  useEffect(() => {
    if (
      !hasInitializedHome.current &&
      homePageData != null &&
      homeDefaultValues &&
      Object.keys(homeDefaultValues).length > 0
    ) {
      resetHome(homeDefaultValues);
      hasInitializedHome.current = true;
    }
  }, [resetHome, homeDefaultValues, homePageData]);

  const onSubmitHome = (submittedData: CmsHomepageForm) => {
    if (instanceData?.CMS?.id == null) {
      console.error("Tried to submit but there is no CMS");
      return;
    }

    Object.keys(homeForm.formState.dirtyFields ?? {}).forEach((key) => {
      const localeData = submittedData[key as SupportedLocale];
      const baseData = homePageData?.[key as SupportedLocale];
      if (!localeData) return;

      const mutation = baseData?.content.id ? update : create;
      const websiteTitle = localeData.websiteTitle ?? "";
      mutation.mutate(
        {
          id: baseData?.content.id ?? "",
          lang: key as SupportedLocale,
          data: buildCmsPagePatchPayload({
            pageName: websiteTitle,
            published: localeData.published,
            pageBackgroundColorId: localeData.pageBackgroundColorId,
            pageTextColorMode: localeData.pageTextColorMode,
            pageTextColorId: localeData.pageTextColorId,
            pageFontFamily: localeData.pageFontFamily,
            pageFontVariant: localeData.pageFontVariant,
            content: {
              websiteTitle,
              websiteSubtitle: localeData.websiteSubtitle,
              catalogueRaisonneCTA: localeData.catalogueRaisonneCTA,
              artistName: localeData.artistName,
              birthDate: localeData.birthDate,
              deathDate: localeData.deathDate,
              credits: localeData.credits,
              footerArchives: localeData.footerArchives,
              resume: localeData.resume,
            },
          }),
        },
        {
          onSuccess: () => {
            toast({
              title: t("success"),
              description: t("homepageSuccessMessage"),
              duration: 4000,
              variant: "success",
            });
          },
          onError: () => {
            toast({
              title: t("error"),
              description: t("homepageErrorMessage"),
              variant: "error",
              duration: 4000,
            });
          },
        },
      );
    });
  };

  return (
    <CMSLayout
      pageName={t("cmsTabAccueil")}
      formIds={["homepage"]}
      className="flex flex-col gap-4"
    >
      <FormProvider {...homeForm}>
        <FormFieldVisibilityProvider formKey="cms-homepage">
          <CmsPageAppearanceConfig formId="homepage" />
          <form
            onSubmit={handleSubmitMultilingualForm(
              homeForm,
              homePageData,
              onSubmitHome,
            )}
            id="homepage"
            className="flex w-full flex-col gap-6"
          >
            <div className="flex w-full gap-4">
              <DoubleMultilingualField
                className="w-full"
                corner={(lang) => (
                  <Switch dispLabel={false} name={`${lang}.published`} />
                )}
                render={(lang) => <HomepageForm language={lang} />}
              />
            </div>
          </form>
        </FormFieldVisibilityProvider>
      </FormProvider>
    </CMSLayout>
  );
};

export default CMS;
